import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ModuleItem, ReportAssignmentRow, ReportTypeDefinition } from "./types";
import { apiErrorMessage } from "./logic";
import { RaIcon } from "./RaIcon";

interface ModulePickerPopoverProps {
  anchor: HTMLElement;
  row: ReportAssignmentRow;
  reportType: ReportTypeDefinition;
  bomName: string;
  bomModules: ModuleItem[];
  /** Saves the selection (PUT assignments/<id>/modules/); rejects with the API error. */
  onSave: (nextModuleIds: number[], reason?: string) => Promise<unknown>;
  onClose: () => void;
}

/**
 * Section 2's module picker: tick which modules feed a report, with a
 * required reason when editing an already-confirmed (locked) report.
 */
export const ModulePickerPopover: React.FC<ModulePickerPopoverProps> = ({
  anchor,
  row,
  reportType,
  bomName,
  bomModules,
  onSave,
  onClose,
}) => {
  const popRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<Set<number>>(() => new Set(row.moduleIds));
  const [search, setSearch] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const initialKey = useMemo(() => row.moduleIds.slice().sort((a, b) => a - b).join(","), [row.moduleIds]);

  useLayoutEffect(() => {
    const pop = popRef.current;
    if (!pop) return;
    const b = anchor.getBoundingClientRect();
    const h = pop.offsetHeight;
    const left = Math.max(12, Math.min(window.innerWidth - pop.offsetWidth - 12, b.right - pop.offsetWidth));
    const top = b.bottom + 6 + h > window.innerHeight ? Math.max(12, b.top - h - 6) : b.bottom + 6;
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }, [anchor]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || anchor.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [anchor, onClose]);

  const tsdText = (m: ModuleItem) => (m.tsds.length ? m.tsds.map((t) => t.name).join(", ") : "Spare");

  const q = search.trim().toLowerCase();
  const visible = bomModules.filter(
    (m) => !q || `${m.serial} ${m.tsds.map((t) => `TSD-${t.id} ${t.name}`).join(" ")} ${m.spare ? "spare" : ""}`.toLowerCase().includes(q),
  );

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setVisible = (on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      visible.forEach((m) => (on ? next.add(m.id) : next.delete(m.id)));
      return next;
    });

  const confirmed = row.confirmed;
  const currentKey = Array.from(selected).sort((a, b) => a - b).join(",");
  const changed = currentKey !== initialKey;
  const trimmedReason = reason.trim();
  const saveDisabled =
    saving || !changed || (confirmed && !(trimmedReason && selected.size > 0));
  const saveTitle = !saveDisabled
    ? undefined
    : !changed
      ? "Change at least one module"
      : !selected.size
        ? "Pick at least one module"
        : "Add a reason";

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave(Array.from(selected), confirmed ? trimmedReason : undefined);
      onClose();
    } catch (e: any) {
      setError(apiErrorMessage(e, "Could not save the modules"));
      setSaving(false);
    }
  };

  return (
    <div ref={popRef} className="pop open" role="dialog" aria-label="Choose modules">
      <div className="phd">
        <b>
          {reportType.name} · {bomName}
        </b>
        <span>Tick the modules this report should use. Each one shows its TSD and inspection result.</span>
      </div>
      {confirmed && (
        <div className="pwarn">
          <RaIcon name="lock" />
          This report is confirmed. Your change applies straight away and is saved in the history.
        </div>
      )}
      <div className="pt">
        <div className="psrch">
          <RaIcon name="search" />
          <input
            placeholder="Search serial or TSD"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button type="button" className="lnk3" onClick={() => setVisible(true)}>
          Select all
        </button>
        <button type="button" className="lnk3" onClick={() => setVisible(false)}>
          Clear
        </button>
      </div>
      <div className="plh4">
        <span />
        <span>Serial number</span>
        <span>TSD</span>
        <span>Inspection</span>
      </div>
      <div className="pl">
        {visible.map((m) => (
  <label 
    key={m.id} 
    className="po f4" 
    style={{ alignItems: "flex-start" }} // Ensures checkbox stays at the top when text wraps
  >
    <input 
      type="checkbox" 
      checked={selected.has(m.id)} 
      onChange={() => toggle(m.id)} 
      style={{ marginTop: 4 }} // Slight adjustment to align with the first line of text
    />
    <span className="mono" style={{ marginTop: 2 }}>{m.serial}</span>
    
    <span 
      className="tsd2" 
      title={tsdText(m)}
      style={{
        whiteSpace: "normal", // Allows the text to wrap to the next line
        wordBreak: "break-word", // Ensures long words don't overflow
        paddingRight: 12,
        lineHeight: 1.4 // Adds a little breathing room between wrapped lines
      }}
    >
      {m.tsds.length > 0 && <span className="tb">TSD-{m.tsds[0].id}</span>}
      {tsdText(m)}
    </span>
    
    <span 
      className={`ins ${m.passedInspection ? "ok" : "no"}`}
      style={{ marginTop: 2, textAlign: "center" }}
    >
      {m.passedInspection ? "Pass" : "Not pass"}
    </span>
  </label>
))}
        {visible.length === 0 && <div className="hempty">No modules found.</div>}
      </div>
      {confirmed && (
        <div className="pwhy">
          <label htmlFor="ra3-why">
            Why are you changing it? <em className="req">Required</em>
          </label>
          <input
            id="ra3-why"
            placeholder="e.g. Wrong module mapped"
            autoComplete="off"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      )}
      {error && <div className="perr">{error}</div>}
      <div className="pf">
        <span className="pcount">
          {selected.size} of {bomModules.length} selected
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn sm" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className="btn sm pr"
          disabled={saveDisabled}
          title={saveTitle}
          onClick={handleSave}
        >
          {saving ? "Saving…" : confirmed ? "Save change" : "Save"}
        </button>
      </div>
    </div>
  );
};
